import React, { useState } from 'react';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { useToast } from '../../../components/ui/ToastProvider';
import {
  openPrintShopMaps,
  printShopMapsUrl,
} from '../utils/mapPrintShopSearch';
import '../pages/printable-map-shell.css';

function defaultLocate(onSuccess, onError) {
  if (!navigator.geolocation) {
    onError({ code: 0, message: 'Geolocation is not available.' });
    return;
  }
  navigator.geolocation.getCurrentPosition(onSuccess, onError, {
    enableHighAccuracy: false,
    timeout: 10000,
    maximumAge: 60000,
  });
}

/**
 * Opens a Maps search for retail print shops. Three origins, always visible:
 * the user's location (the only permission prompt), the printed land, or a
 * city / ZIP they type. Yona is not the merchant of record.
 */
export function PrintableMapShopFinder({
  landCentroid,
  locate = defaultLocate,
  openWindow,
}) {
  const { show: showToast } = useToast();
  const [place, setPlace] = useState('');
  const [locating, setLocating] = useState(false);
  const placeInputId = 'pm-shop-place';

  const focusPlace = () => {
    const field = document.getElementById(placeInputId);
    if (field) field.focus();
  };

  const openFrom = (origin) => {
    const url = printShopMapsUrl(origin);
    if (!url) return false;
    return openPrintShopMaps(url, openWindow);
  };

  const handleNearMe = () => {
    setLocating(true);
    locate(
      (position) => {
        setLocating(false);
        openFrom({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        setLocating(false);
        showToast('Location is off — enter a city or ZIP.', { variant: 'warning' });
        focusPlace();
      }
    );
  };

  const handleNearLand = () => {
    openFrom({
      lat: landCentroid.lat,
      lng: landCentroid.lng,
      roundPlace: true,
    });
  };

  const handlePlace = () => {
    if (!openFrom({ place })) {
      showToast('Enter a city or ZIP to find shops.', { variant: 'warning' });
      focusPlace();
    }
  };

  return (
    <div className="pm-guidance__partner" data-testid="pm-print-partner-slot">
      <div className="pm-mono-label">Find a print shop</div>
      <p className="pm-shop-finder__lede">
        Opens Maps with Staples, FedEx Office, and Office Depot. Download the PDF
        first, then print at 100% / actual size. You pay the shop.
      </p>
      <div className="pm-shop-finder__actions">
        <YButton
          type="button"
          variant="secondary"
          disabled={locating}
          onClick={handleNearMe}
          data-testid="pm-shop-near-me"
        >
          {locating ? 'Finding you…' : 'Near me'}
        </YButton>
        <YButton
          type="button"
          variant="secondary"
          disabled={!landCentroid}
          onClick={handleNearLand}
          data-testid="pm-shop-near-land"
        >
          Near this property
        </YButton>
      </div>
      <label className="y-label" htmlFor={placeInputId}>City or ZIP</label>
      <div className="pm-shop-finder__place">
        <YInput
          id={placeInputId}
          value={place}
          onChange={(e) => setPlace(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handlePlace();
            }
          }}
          placeholder="29601 or Greenville, SC"
          data-testid="pm-shop-place"
        />
        <YButton
          type="button"
          variant="secondary"
          onClick={handlePlace}
          data-testid="pm-shop-place-go"
        >
          Find shops
        </YButton>
      </div>
    </div>
  );
}
